// Admin live-chat console.
//
// Layout: 2-column. Left = conversation list (sorted by last_msg_at, unread
// badge inline). Right = active conversation (header + scrollable history +
// composer). Top of left column has the agent's status switcher
// (online / busy / offline) + a sound-alert toggle.
//
// All chat traffic is one Durable Object. We open ONE WebSocket per tab to
// `/api/chat/ws` (proxied by the Pages Function to api.fuelio.net) using a
// short-lived chat-ws JWT in the Sec-WebSocket-Protocol header.

import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, Bell, BellOff, Image as ImageIcon, Loader2, Send } from 'lucide-react'
import { api, errorMessage, API_BASE } from '@/lib/api'
import { useI18n } from '@/lib/i18n'
import { fmtRelative } from '@/lib/format'
import { cn } from '@/lib/cn'

type AgentStatus = 'online' | 'busy' | 'offline'

interface Conversation {
  id: string
  userId: string
  lastMsgAt: number
  unreadAdmin: number
  email: string | null
  displayName: string | null
}

interface Message {
  id: string
  conversationId: string
  sender: 'user' | 'admin'
  kind: 'text' | 'image'
  body: string
  createdAt: number
}

const SOUND_PREF_KEY = 'fuelio.admin.chat.sound'

export function ChatScreen() {
  const { t } = useI18n()
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [agentStatus, setAgentStatus] = useState<AgentStatus>('online')
  const [connState, setConnState] = useState<'connecting' | 'open' | 'closed'>('connecting')
  const [draft, setDraft] = useState('')
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [soundOn, setSoundOn] = useState<boolean>(() => {
    if (typeof localStorage === 'undefined') return true
    return localStorage.getItem(SOUND_PREF_KEY) !== '0'
  })

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttempt = useRef(0)
  const reconnectTimer = useRef<number | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  // The WS message handler is set up once (inside connect()) and captures
  // its `selectedId` / `soundOn` lexically — without refs those would be
  // forever-stale, breaking auto-append when a new message lands on the
  // currently-focused conversation. Mirror the latest values through refs.
  const selectedIdRef = useRef<string | null>(null)
  const soundOnRef = useRef<boolean>(true)
  useEffect(() => { selectedIdRef.current = selectedId }, [selectedId])
  useEffect(() => { soundOnRef.current = soundOn }, [soundOn])

  // ─── connection ─────────────────────────────────────────────────────────
  useEffect(() => {
    void connect()
    return () => {
      if (reconnectTimer.current) window.clearTimeout(reconnectTimer.current)
      if (wsRef.current) {
        wsRef.current.onclose = null
        wsRef.current.close(1000, 'unmount')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Initial inbox.
  useEffect(() => {
    void api.chatConversations().then((r) => setConversations(r.conversations)).catch((e) => {
      setError(errorMessage(e))
    })
  }, [])

  async function connect() {
    setConnState('connecting')
    try {
      const r = await api.chatWsToken()
      // Build wss:// URL from the same `/api` proxy. /api/* on admin.fuelio.net
      // is forwarded to api.fuelio.net by the Pages Function — that proxy
      // passes through WebSocket upgrades.
      const httpBase = location.origin + API_BASE
      const wsBase = httpBase.replace(/^http:/i, 'ws:').replace(/^https:/i, 'wss:')
      const ws = new WebSocket(`${wsBase}/chat/ws`, [`Bearer.${r.token}`])
      wsRef.current = ws
      ws.addEventListener('open', () => {
        setConnState('open')
        reconnectAttempt.current = 0
      })
      ws.addEventListener('message', (e) => {
        try {
          const data = JSON.parse(typeof e.data === 'string' ? e.data : '')
          handleServer(data)
        } catch { /* ignore */ }
      })
      ws.addEventListener('close', (ev) => {
        setConnState('closed')
        if (ev.code !== 1000) scheduleReconnect()
      })
    } catch (e) {
      setError(errorMessage(e))
      scheduleReconnect()
    }
  }

  function scheduleReconnect() {
    if (reconnectTimer.current) return
    const delay = Math.min(1000 * Math.pow(2, reconnectAttempt.current), 30_000)
    reconnectAttempt.current += 1
    reconnectTimer.current = window.setTimeout(() => {
      reconnectTimer.current = null
      void connect()
    }, delay)
  }

  function handleServer(m: any) {
    switch (m.type) {
      case 'hello':
        setAgentStatus(m.agentStatus)
        return
      case 'agentStatus':
        setAgentStatus(m.status)
        return
      case 'msg': {
        // Read the latest selected-conversation id from the ref — the closure
        // version was captured when connect() ran and is forever-stale.
        const focused = selectedIdRef.current
        // If this msg is for the conv we're focused on, append + auto-mark
        // read. Otherwise just bump the conversation list (refetch once).
        if (m.conversationId === focused) {
          setMessages((prev) => {
            const idx = prev.findIndex((x) => x.id.startsWith('tmp:') && x.sender === m.sender && x.kind === m.kind && x.body === m.body)
            if (idx >= 0) {
              const next = prev.slice()
              next[idx] = m
              return next
            }
            return [...prev, m]
          })
          wsRef.current?.send(JSON.stringify({ type: 'read', conversationId: m.conversationId }))
        }
        // Bump conversation order + unread badge.
        setConversations((prev) => {
          const idx = prev.findIndex((c) => c.id === m.conversationId)
          if (idx < 0) return prev
          const updated: Conversation = {
            ...prev[idx]!,
            lastMsgAt: m.createdAt,
            unreadAdmin: m.conversationId === focused
              ? prev[idx]!.unreadAdmin
              : (m.sender === 'user' ? prev[idx]!.unreadAdmin + 1 : prev[idx]!.unreadAdmin),
          }
          // Maybe-notify: tab not focused + sound on + message from user.
          if (m.sender === 'user' && m.conversationId !== focused) {
            maybeBeep(soundOnRef.current)
            maybeFlashTitle(prev.filter((c) => c.id !== m.conversationId).reduce((a, c) => a + c.unreadAdmin, 0) + updated.unreadAdmin)
          }
          const next = [updated, ...prev.filter((c) => c.id !== m.conversationId)]
          return next
        })
        return
      }
      case 'unread':
        setConversations((prev) => prev.map((c) => c.id === m.conversationId ? { ...c, unreadAdmin: m.n } : c))
        return
    }
  }

  // ─── conversation selection ─────────────────────────────────────────────
  async function selectConversation(c: Conversation) {
    setSelectedId(c.id)
    setMessages([])
    try {
      const r = await api.chatMessages(c.id, { limit: 50 })
      setMessages(r.messages)
    } catch (e) {
      setError(errorMessage(e))
    }
    wsRef.current?.send(JSON.stringify({ type: 'read', conversationId: c.id }))
    setConversations((prev) => prev.map((x) => x.id === c.id ? { ...x, unreadAdmin: 0 } : x))
  }

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages.length, selectedId])

  // Reset doc-title on focus or when last unread cleared.
  useEffect(() => {
    const onFocus = () => { document.title = 'Fuelio · Admin' }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [])

  // ─── send ────────────────────────────────────────────────────────────────
  function sendText() {
    const body = draft.trim()
    if (!body || !selectedId) return
    const optimisticId = `tmp:${Date.now()}`
    setMessages((prev) => [...prev, {
      id: optimisticId, conversationId: selectedId, sender: 'admin', kind: 'text', body,
      createdAt: Math.floor(Date.now() / 1000),
    }])
    wsRef.current?.send(JSON.stringify({ type: 'send', kind: 'text', body, conversationId: selectedId }))
    setDraft('')
  }

  async function pickAndSendImage(file: File) {
    if (!selectedId) return
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) return
    setUploading(true)
    try {
      const compressed = await compressImage(file)
      const r = await api.chatUploadInit(selectedId, 'image/jpeg', compressed.size)
      const put = await fetch(r.uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': 'image/jpeg' },
        body: compressed,
      })
      if (!put.ok) throw new Error('upload_failed')
      const optimisticId = `tmp:${Date.now()}`
      setMessages((prev) => [...prev, {
        id: optimisticId, conversationId: selectedId, sender: 'admin', kind: 'image', body: r.key,
        createdAt: Math.floor(Date.now() / 1000),
      }])
      wsRef.current?.send(JSON.stringify({ type: 'send', kind: 'image', body: r.key, conversationId: selectedId }))
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setUploading(false)
    }
  }

  // ─── status switcher ────────────────────────────────────────────────────
  function setStatus(status: AgentStatus) {
    wsRef.current?.send(JSON.stringify({ type: 'setStatus', status }))
    setAgentStatus(status)
  }

  function toggleSound() {
    setSoundOn((v) => {
      const next = !v
      try { localStorage.setItem(SOUND_PREF_KEY, next ? '1' : '0') } catch { /* ignore */ }
      return next
    })
  }

  const totalUnread = useMemo(
    () => conversations.reduce((a, c) => a + c.unreadAdmin, 0),
    [conversations],
  )

  const selectedConv = useMemo(
    () => conversations.find((c) => c.id === selectedId) ?? null,
    [conversations, selectedId],
  )

  return (
    // mobile：list 和 active conversation 互斥（selectedId null=列表全屏，
    // 非 null=聊天全屏）。md+ 上恢复双栏。Tailwind 用 hidden / md:flex 切。
    <div className="flex h-full overflow-hidden">
      {/* ─── left column: conversations ───────────────────────────── */}
      <aside
        className={cn(
          'shrink-0 border-r border-border bg-card flex-col',
          'md:flex md:w-72',
          // mobile：选中会话时整个 list 隐藏让 active 全屏；未选中时自占满宽度
          selectedId ? 'hidden' : 'flex w-full',
        )}
      >
        <header className="px-3 py-3 border-b border-border space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-base font-semibold">{t('chat.title')}</h1>
            <button
              onClick={toggleSound}
              className="text-muted-foreground hover:text-foreground p-1 rounded"
              title={t('chat.notify')}
            >
              {soundOn ? <Bell size={15}/> : <BellOff size={15}/>}
            </button>
          </div>
          <div className="flex gap-1">
            {(['online','busy','offline'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={cn(
                  'flex-1 text-xs rounded px-2 py-1.5 border transition-colors',
                  agentStatus === s
                    ? s === 'online' ? 'bg-success/15 text-success border-success/30 font-medium'
                      : s === 'busy' ? 'bg-warning/15 text-warning border-warning/30 font-medium'
                      : 'bg-muted text-muted-foreground border-border font-medium'
                    : 'bg-transparent text-muted-foreground border-border hover:bg-muted',
                )}
              >
                {t(`chat.status${s.charAt(0).toUpperCase()}${s.slice(1)}` as any)}
              </button>
            ))}
          </div>
          {connState !== 'open' && (
            <p className="text-[11px] text-warning flex items-center gap-1">
              <Loader2 size={11} className="animate-spin"/> {t('chat.disconnected')}
            </p>
          )}
          {totalUnread > 0 && (
            <p className="text-[11px] text-muted-foreground">{t('chat.unread')}: {totalUnread}</p>
          )}
        </header>

        <ul className="flex-1 overflow-y-auto">
          {conversations.length === 0
            ? <li className="p-6 text-sm text-muted-foreground text-center">{t('chat.empty')}</li>
            : conversations.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => void selectConversation(c)}
                    className={cn(
                      'w-full text-left px-3 py-3 border-b border-border/50 hover:bg-muted transition-colors',
                      selectedId === c.id && 'bg-primary/10',
                    )}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium text-sm truncate">
                        {c.displayName || c.email || c.userId.slice(-8)}
                      </span>
                      <span className="text-[10px] text-muted-foreground shrink-0">
                        {fmtRelative(c.lastMsgAt)}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{c.email || c.userId}</div>
                    {c.unreadAdmin > 0 && (
                      <span className="inline-block mt-1 px-1.5 py-0.5 rounded-full bg-danger text-danger-foreground text-[10px] font-medium">
                        {c.unreadAdmin}
                      </span>
                    )}
                  </button>
                </li>
              ))}
        </ul>
      </aside>

      {/* ─── right column: active conversation ────────────────────── */}
      <section
        className={cn(
          'flex-1 flex-col min-w-0',
          'md:flex',
          // mobile：没选中会话时整个 section 隐藏（让 list 全屏）；选中后才显示
          selectedConv ? 'flex' : 'hidden',
        )}
      >
        {selectedConv ? (
          <>
            <header className="px-4 py-3 border-b border-border bg-card flex items-center gap-3">
              {/* mobile-only 返回按钮：清掉 selectedId 让用户回到列表 */}
              <button
                type="button"
                onClick={() => { setSelectedId(null); setMessages([]) }}
                aria-label="back"
                className="md:hidden -ml-1 p-1.5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                <ArrowLeft size={18} strokeWidth={2}/>
              </button>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">
                  {selectedConv.displayName || selectedConv.email || selectedConv.userId.slice(-8)}
                </div>
                <div className="text-xs text-muted-foreground truncate">{selectedConv.email}</div>
              </div>
            </header>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 bg-background">
              {messages.map((m) => (
                <AdminBubble key={m.id} m={m}/>
              ))}
            </div>

            <div className="border-t border-border bg-card p-3 flex items-end gap-2">
              <label className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md border border-border text-muted-foreground hover:bg-muted cursor-pointer" title={t('chat.image')}>
                <ImageIcon size={16}/>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) void pickAndSendImage(f)
                    e.target.value = ''
                  }}
                />
              </label>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    sendText()
                  }
                }}
                rows={1}
                placeholder={t('chat.placeholder')}
                className="flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm outline-none max-h-32"
              />
              <button
                onClick={sendText}
                disabled={!draft.trim()}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                <Send size={14}/> {t('chat.send')}
              </button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
            {t('chat.selectConv')}
          </div>
        )}
        {error && <div className="px-4 py-2 text-xs text-danger border-t border-border">{error}</div>}
      </section>
    </div>
  )
}

function AdminBubble({ m }: { m: Message }) {
  const isAdmin = m.sender === 'admin'
  return (
    <div className={cn('flex mb-2', isAdmin ? 'justify-end' : 'justify-start')}>
      <div className={cn(
        'max-w-[78%] text-sm leading-snug',
        m.kind === 'text' ? 'px-3 py-2 rounded-lg' : 'rounded-lg overflow-hidden',
        isAdmin
          ? 'bg-primary text-primary-foreground'
          : 'bg-muted text-foreground',
      )}>
        {m.kind === 'text'
          ? m.body
          : <img src={`${API_BASE}/chat/admin/image/${m.body}`} alt="" className="block max-w-full" loading="lazy"/>}
      </div>
    </div>
  )
}

// ─── client-side helpers ────────────────────────────────────────────────────

async function compressImage(file: File): Promise<Blob> {
  const MAX_EDGE = 1600
  const QUALITY = 0.8
  const bmp = await createImageBitmap(file)
  const scale = Math.min(1, MAX_EDGE / Math.max(bmp.width, bmp.height))
  const w = Math.round(bmp.width * scale)
  const h = Math.round(bmp.height * scale)
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return file
  ctx.drawImage(bmp, 0, 0, w, h)
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', QUALITY))
  return blob ?? file
}

let lastBeep = 0
function maybeBeep(soundOn: boolean) {
  if (!soundOn) return
  const now = Date.now()
  if (now - lastBeep < 2000) return // throttle
  lastBeep = now
  try {
    // 200ms 880Hz beep via WebAudio — no audio file required.
    const Ctx = (window.AudioContext || (window as any).webkitAudioContext)
    const ac: AudioContext = new Ctx()
    const osc = ac.createOscillator()
    const gain = ac.createGain()
    osc.frequency.value = 880
    osc.connect(gain)
    gain.connect(ac.destination)
    gain.gain.setValueAtTime(0.0001, ac.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.15, ac.currentTime + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + 0.2)
    osc.start()
    osc.stop(ac.currentTime + 0.2)
    osc.onended = () => ac.close().catch(() => {})
  } catch { /* ignore */ }
}

let titleTimer: number | null = null
function maybeFlashTitle(unread: number) {
  if (document.hasFocus()) return
  if (titleTimer) return
  let on = false
  titleTimer = window.setInterval(() => {
    document.title = on ? 'Fuelio · Admin' : `(${unread}) Fuelio · Admin`
    on = !on
  }, 1000)
  const stop = () => {
    if (titleTimer) { window.clearInterval(titleTimer); titleTimer = null }
    document.title = 'Fuelio · Admin'
    window.removeEventListener('focus', stop)
  }
  window.addEventListener('focus', stop)
}
